import { Button, Descriptions, Drawer, Space, Spin, Timeline, Typography } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useSlaEventDetail } from '@/modules/sla/queries'
import { getSlaRuleName, getSlaTargetRoleLabel } from '@/modules/sla/labels'
import StatusTag from '@/components/feedback/StatusTag'
import { formatDateTime } from '@/utils/datetime'

const { Text, Title } = Typography

interface Props {
  eventId: string | null
  onClose: () => void
}

export default function SlaEventDetailDrawer({ eventId, onClose }: Props) {
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useSlaEventDetail(eventId)

  const errorMessage = error instanceof Error ? error.message : '加载失败'

  const goTo = (path: string) => {
    onClose()
    navigate(path)
  }

  return (
    <Drawer
      title="超时提醒详情"
      size="large"
      open={!!eventId}
      onClose={onClose}
      destroyOnHidden
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : isError ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Text type="danger">{errorMessage}</Text>
        </div>
      ) : data ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="事件ID">{data.id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <StatusTag type="sla" status={data.event_status} />
            </Descriptions.Item>
            <Descriptions.Item label="规则编码">{data.rule_code}</Descriptions.Item>
            <Descriptions.Item label="超时类型">{getSlaRuleName(data.rule_code, data.rule_name)}</Descriptions.Item>
            <Descriptions.Item label="映射ID">{data.mapping_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="平台阶段">{data.platform_status || '-'}</Descriptions.Item>
            <Descriptions.Item label="工单标题" span={2}>
              {data.task_title || <Text type="secondary">-</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="提醒对象">{getSlaTargetRoleLabel(data.target_role)}</Descriptions.Item>
            <Descriptions.Item label="责任人">{data.target_account}</Descriptions.Item>
            <Descriptions.Item label="首次命中">{formatDateTime(data.first_detected_at)}</Descriptions.Item>
            <Descriptions.Item label="当前阶段">
              {data.workflow_summary?.current_stage || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="当前升级级别">
              {data.current_escalation_level >= 2 ? 'L2 二级升级' : data.current_escalation_level === 1 ? 'L1 一级提醒' : '未提醒'}
            </Descriptions.Item>
            <Descriptions.Item label="提醒次数">{data.remind_count}</Descriptions.Item>
            <Descriptions.Item label="一级提醒时间">
              {data.level1_sent_at ? formatDateTime(data.level1_sent_at) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="二级提醒时间">
              {data.level2_sent_at ? formatDateTime(data.level2_sent_at) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="二级抄送" span={2}>
              {data.escalation_enabled ? data.escalation_cc_emails || 'qhdet@cleartv.cn' : '未启用'}
            </Descriptions.Item>
            <Descriptions.Item label="当前卡点" span={2}>
              {data.workflow_summary?.bottleneck_reason_text || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="下一步动作" span={2}>
              {data.workflow_summary?.next_action_label || '-'}
            </Descriptions.Item>
          </Descriptions>

          {(data.quick_links?.message || data.quick_links?.draft || data.quick_links?.ticket || data.quick_links?.workflow_trace) && (
            <div>
              <Title level={5}>快速跳转</Title>
              <Space wrap>
                {data.related_entities?.message?.visible && data.quick_links?.message && (
                  <Button onClick={() => goTo(`/messages?messageId=${encodeURIComponent(data.quick_links!.message!.message_id)}`)}>
                    查看消息
                  </Button>
                )}
                {data.related_entities?.draft?.visible && data.quick_links?.draft && (
                  <Button onClick={() => goTo(`/drafts/${data.quick_links!.draft!.draft_id}`)}>
                    查看草稿
                  </Button>
                )}
                {data.related_entities?.ticket?.visible && data.quick_links?.ticket && (
                  <Button onClick={() => goTo(`/tickets/${data.quick_links!.ticket!.mapping_id}`)}>
                    查看工单
                  </Button>
                )}
                {data.quick_links?.workflow_trace && (
                  <Button onClick={() => goTo(`/workflow-traces/${data.quick_links!.workflow_trace!.entry_type}/${data.quick_links!.workflow_trace!.entry_id}`)}>
                    查看流程
                  </Button>
                )}
              </Space>
            </div>
          )}

          {/* 提醒历史 */}
          {data.remind_history && data.remind_history.length > 0 && (
            <div>
              <Title level={5}>提醒历史</Title>
              <Timeline>
                {data.remind_history.map((h, i) => (
                  <Timeline.Item key={i}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {h.channel} - {h.created_at}
                    </Text>
                    <br />
                    <Text>{h.note}</Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            </div>
          )}

          {/* 可用操作 */}
          {data.operator_actions && data.operator_actions.length > 0 && (
            <div>
              <Title level={5}>可用操作</Title>
              <Space orientation="vertical" size={0} style={{ width: '100%' }}>
                {data.operator_actions.map((action) => (
                  <div
                    key={action.action}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '8px 0',
                    }}
                  >
                    <Text>{action.label}</Text>
                    <Text type={action.enabled ? 'success' : 'secondary'}>
                      {action.enabled ? '可用' : '不可用'}
                    </Text>
                  </div>
                ))}
              </Space>
            </div>
          )}
        </Space>
      ) : null}
    </Drawer>
  )
}
